import {
  AspectRatio,
  Box,
  BoxProps,
  HStack,
  Image,
  Text,
} from "@chakra-ui/react";
import EtherPrice from "components/EtherPrice";
import LevelBadge from "components/LevelBadge";
import SupplyBadge from "components/SupplyBadge";
import TraitImage from "components/TraitImage";
import { publicUrl } from "config";
import { traitCategories } from "config/categories";
import { BigNumber } from "ethers";
import { ImageSize } from "types";
import { capitalize } from "utils/stringUtils";
import { getTraitCategory } from "utils/traitsUtils";

export type TraitTileProps = Omit<BoxProps, "onChange"> & {
  tokenId: number;
  name?: string;
  level?: number;
  supply?: [current: number, max: number];
  price?: BigNumber;
  canvasSize?: BoxProps["w"];
  canvasBg?: BoxProps["bg"];
  shapeOpacity?: number;
  imageSize?: ImageSize;
  isDisabled?: boolean;
  onClick?: (tokenId: number) => void;
};

const TraitTile: React.FC<TraitTileProps> = ({
  tokenId,
  price,
  name,
  level,
  supply,
  canvasSize = "12",
  canvasBg = "dark.400",
  imageSize = "256",
  shapeOpacity,
  children,
  isDisabled = false,
  onClick,
  ...rest
}) => {
  const category = getTraitCategory(tokenId);

  return (
    <Box
      bg="dark.800"
      p="3"
      w="full"
      userSelect="none"
      borderRadius="md"
      cursor={isDisabled ? "not-allowed" : onClick ? "pointer" : undefined}
      transition="all 0.15s"
      color="bright.50"
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
      onClick={() => {
        if (!isDisabled) {
          onClick?.(tokenId);
        }
      }}
      {...rest}
    >
      <HStack spacing="3" w="full" align="center">
        <AspectRatio
          ratio={1}
          bg={canvasBg}
          w={canvasSize}
          h={canvasSize}
          flex="none"
          borderRadius="md"
        >
          <>
            <Image
              src={`${publicUrl}/v2-placeholder_transparent.png`}
              w="full"
              transition="opacity 0.15s"
              position="absolute"
              zIndex={category === traitCategories[0] ? 10 : 0}
              opacity={shapeOpacity}
            />
            <TraitImage
              tokenId={tokenId}
              size={imageSize}
              position="absolute"
              zIndex={5}
            />
          </>
        </AspectRatio>

        <Box w="full" minW="0" whiteSpace="nowrap">
          <Text fontWeight="semibold" overflow="hidden" textOverflow="ellipsis">
            {name} #{tokenId}
          </Text>
          <HStack spacing="1.5">
            <Text
              fontSize="xs"
              opacity={0.5}
              overflow="hidden"
              textOverflow="ellipsis"
              isTruncated
            >
              {capitalize(category)}
            </Text>

            {supply && (
              <SupplyBadge current={supply[0]} max={supply[1]} size="sm" />
            )}

            {!!level && <LevelBadge level={level} size="sm" />}
          </HStack>
        </Box>

        {price && (
          <EtherPrice
            amount={price}
            asPrice
            color="secondary.500"
            fontWeight="semibold"
          />
        )}

        {children && <Box flex="none">{children}</Box>}
      </HStack>
    </Box>
  );
};

export default TraitTile;
